import {perfLogger} from '@/buried/performance';
import weirwood from '@/weirwood';
import {Component, ReactNode} from 'react';

interface ErrorBoundaryProps {
    children?: ReactNode;
    isRemote?: boolean;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

export default class LoadableErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    /**
     * @class ErrorBoundary
     * @extends React.Component
     */
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {hasError: false};
    }

    /**
     * 获取从错误派生的状态。
     * 如果有错误，则返回一个包含 `hasError` 属性为 `true` 的对象。
     *
     * @returns 包含 `hasError` 属性为 `true` 的对象。
     */
    static getDerivedStateFromError() {
        return {hasError: true};
    }

    /**
     * 捕获错误并上报。
     *
     * @param error - 抛出的异常对象或错误信息。
     */
    componentDidCatch(error: Error) {
        try {
            const captureError = perfLogger.captureError;
            if (typeof error === 'string' || !error) {
                error = new Error(error);
            }
            error.message = `LoadableErrorBoundary  ${error.message}`;
            if (this.props.isRemote) {
                error.message = `RemoteLoadableErrorBoundary  ${error.message}`;
            }
            weirwood && weirwood.error.captureException(error);
            // 未被try catch住的error
            captureError.captureError(error, captureError.ERROR_TYPE.unCapturedError);

        } catch (err) {
            console.error('err: ', err);
        }
    }

    /**
     * 返回渲染的子组件。
     * 如果发生错误，则返回一个空的 div 元素；
     * 否则，返回 props 中的子组件。
     *
     * @method render
     * @returns {React.Component} - 如果发生错误，返回一个空的 div 元素；否则，返回 props 中的子组件。
     */
    render() {
        if (this.state.hasError) {
            return <div></div>;
        }
        return this.props.children;
    }
}
